import {computed, nextTick, ref} from 'vue';
import {defineStore} from 'pinia';
import {useTranslation} from '#imports';
import type {TJoySelectableItemOption} from '@maltjoy/core-vue';

export type QuestionId = 'what bring you there' | 'when should the freelancer start' | 'how many freelancers do you work with on a monthly basics';
export type AnswerId =
    | 'find freelancers'
    | 'browse and comparing'
    | 'work with freelancers i already know'
    | 'monitor my collaborator activity'
    | 'asap'
    | '1 or 2 weeks'
    | 'more than 2 weeks'
    | 'not sure'
    | '1 freelancer'
    | '2-3 freelancers'
    | '4-7 freelancers'
    | 'more than 7'
    | 'not plan to work with freelancers regularly';

export type Question = {
    id: QuestionId;
    title: string;
    subtitle?: string;
    options: TJoySelectableItemOption[];
    answersModel: AnswerId | AnswerId[];
    active: boolean;
    activateOnlyIf?: (questions: Question[]) => boolean;
    multiple: boolean;
};

export const useUserQualificationStore = defineStore('user-qualification-store', () => {
    const {t} = useTranslation();

    const questions = ref<Question[]>([
        {
            id: 'what bring you there',
            title: t('signup.client.user.qualification.page.question.what.brings.you.there.title'),
            subtitle: t('signup.client.user.qualification.page.question.what.brings.you.there.subtitle'),
            options: [
                {
                    value: 'find freelancers',
                    id: 'find freelancers',
                    label: t('signup.client.user.qualification.page.question.what.brings.you.there.answer.find.freelancers'),
                    attrs: {'data-testid': 'user-qualification-page-question-1-answer-1'},
                },
                {
                    value: 'browse and comparing',
                    id: 'browse and comparing',
                    label: t('signup.client.user.qualification.page.question.what.brings.you.there.answer.browse.and.comparing'),
                    attrs: {'data-testid': 'user-qualification-page-question-1-answer-2'},
                },
                {
                    value: 'work with freelancers i already know',
                    id: 'work with freelancers i already know',
                    label: t('signup.client.user.qualification.page.question.what.brings.you.there.answer.work.with.freelancers.i.already.know'),
                    attrs: {'data-testid': 'user-qualification-page-question-1-answer-3'},
                },
                {
                    value: 'monitor my collaborator activity',
                    id: 'monitor my collaborator activity',
                    label: t('signup.client.user.qualification.page.question.what.brings.you.there.answer.monitor.my.collaborator.activity'),
                    attrs: {'data-testid': 'user-qualification-page-question-1-answer-4'},
                },
            ],
            answersModel: [],
            active: true,
            multiple: true,
        },
        {
            id: 'when should the freelancer start',
            title: t('signup.client.user.qualification.page.question.when.should.the.freelancer.start.title'),
            options: [
                {
                    value: 'asap',
                    id: 'asap',
                    label: t('signup.client.user.qualification.page.question.when.should.the.freelancer.start.answer.asap'),
                    attrs: {'data-testid': 'user-qualification-page-question-2-answer-1'},
                },
                {
                    value: '1 or 2 weeks',
                    id: '1 or 2 weeks',
                    label: t('signup.client.user.qualification.page.question.when.should.the.freelancer.start.answer.1.or.2.weeks'),
                    attrs: {'data-testid': 'user-qualification-page-question-2-answer-2'},
                },
                {
                    value: 'more than 2 weeks',
                    id: 'more than 2 weeks',
                    label: t('signup.client.user.qualification.page.question.when.should.the.freelancer.start.answer.more.than.2.weeks'),
                    attrs: {'data-testid': 'user-qualification-page-question-2-answer-3'},
                },
                {
                    value: 'not sure',
                    id: 'not sure',
                    label: t('signup.client.user.qualification.page.question.when.should.the.freelancer.start.answer.not.sure'),
                    attrs: {'data-testid': 'user-qualification-page-question-2-answer-4'},
                },
            ],
            answersModel: [],
            active: false,
            activateOnlyIf: (questions: Question[]) =>
                ([] as string[]).concat(questions[0].answersModel).find((value: string) => value === 'find freelancers') !== undefined,
            multiple: false,
        },
        {
            id: 'how many freelancers do you work with on a monthly basics',
            title: t('signup.client.user.qualification.page.question.how.many.freelancers.do.you.work.with.on.monthly.basis.title'),
            options: [
                {
                    value: '1 freelancer',
                    id: '1 freelancer',
                    label: t('signup.client.user.qualification.page.question.how.many.freelancers.do.you.work.with.on.monthly.basis.answer.one'),
                    attrs: {'data-testid': 'user-qualification-page-question-3-answer-1'},
                },
                {
                    value: '2-3 freelancers',
                    id: '2-3 freelancers',
                    label: t(
                        'signup.client.user.qualification.page.question.how.many.freelancers.do.you.work.with.on.monthly.basis.answer.two.to.three',
                    ),
                    attrs: {'data-testid': 'user-qualification-page-question-3-answer-2'},
                },
                {
                    value: '4-7 freelancers',
                    id: '4-7 freelancers',
                    label: t(
                        'signup.client.user.qualification.page.question.how.many.freelancers.do.you.work.with.on.monthly.basis.answer.four.to.seven',
                    ),
                    attrs: {'data-testid': 'user-qualification-page-question-3-answer-3'},
                },
                {
                    value: 'more than 7',
                    id: 'more than 7',
                    label: t(
                        'signup.client.user.qualification.page.question.how.many.freelancers.do.you.work.with.on.monthly.basis.answer.more.than.seven',
                    ),
                    attrs: {'data-testid': 'user-qualification-page-question-3-answer-4'},
                },
                {
                    value: 'not plan to work with freelancers regularly',
                    id: 'not plan to work with freelancers regularly',
                    label: t(
                        'signup.client.user.qualification.page.question.how.many.freelancers.do.you.work.with.on.monthly.basis.answer.no.plan.regularly.with.freelancers',
                    ),
                    attrs: {'data-testid': 'user-qualification-page-question-3-answer-5'},
                },
            ],
            answersModel: [],
            active: false,
            multiple: false,
        },
    ]);

    const currentQuestion = computed(() => questions.value.find((question) => question.active));
    const currentQuestionIndex = computed(() => questions.value.findIndex((question) => question.active));
    const currentQuestionIsFirst = computed(() => currentQuestionIndex.value <= 0);
    const currentQuestionIsLast = computed(() => currentQuestionIndex.value === questions.value.length - 1);
    const currentQuestionAnswered = computed(() => (currentQuestion.value?.answersModel.length ?? 0) > 0);
    const lastQuestion = computed(() => questions.value[questions.value.length - 1]);

    async function nextQuestion() {
        for (let i = currentQuestionIndex.value + 1; i < questions.value.length; i++) {
            const nextQuestion = questions.value[i];
            if (!nextQuestion.activateOnlyIf || nextQuestion.activateOnlyIf(questions.value)) {
                await activateQuestion(nextQuestion);
                break;
            }
        }
    }

    async function previousQuestion() {
        for (let i = currentQuestionIndex.value - 1; i >= 0; i--) {
            const previousQuestion = questions.value[i];
            if (!previousQuestion.activateOnlyIf || previousQuestion.activateOnlyIf(questions.value)) {
                await activateQuestion(previousQuestion);
                break;
            }
        }
    }

    async function activateQuestion(question: Question) {
        questions.value[currentQuestionIndex.value].active = false;
        await nextTick();
        question.active = true;
    }

    return {
        currentQuestion,
        currentQuestionIndex,
        currentQuestionIsFirst,
        currentQuestionIsLast,
        currentQuestionAnswered,
        lastQuestion,
        nextQuestion,
        previousQuestion,
        activateQuestion,
    };
});
